import { useState, useMemo, useEffect, useLayoutEffect, useId } from 'react';
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react-dom';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdjustmentScope, AdjustmentSpec, ScopePriceFlags } from './PriceSimulation.types';
import { isRelativeInput, parseDecimalInput, parsePriceInput, parsePercentInput, applyAdjustmentSpec } from './relativePriceInput';
import { Button, ButtonOption, ButtonSize } from '../Fundamentals/Button';
import SkynetIconButton from '../Fundamentals/IconButton/SkynetIconButton';
import SegmentedControl from '../Fundamentals/SegmentedControl/SegmentedControl';
import { useOutsideClick } from '../Fundamentals/_hooks/useOutsideClick';
import { roundToTwoDecimals, sumRounded, formatTwoDecimals, formatPercent, percentChange, toDecimalInputValue, toNumberInputValue } from '../../Utils/decimalUtils';
import { BaseModal as BaseModalJS } from '../Fundamentals/Modal/StandardModal';
import { isTiered } from './priceSimulationModel';

interface PopoverProduct {
    name?: string | null;
    productSku: string;
}

interface PopoverPlan {
    currentPrice: number | null;
    averageDiscount: number | null;
    pricingScheme?: number;
    tierPoints?: { currentUnitPrice: number; isIncluded: boolean }[];
}

interface PopoverGroup {
    name?: string | null;
}

interface RelatedInstance {
    groupName?: string | null;
    productName?: string | null;
    currentPrice: number;
}

interface AdvancedAdjustmentPopoverProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (spec: AdjustmentSpec | null, scope: AdjustmentScope, includedPrice: number | null, keepCurrent: boolean) => void;
    product: PopoverProduct | null;
    plan: PopoverPlan | null;
    group: PopoverGroup | null;
    relatedInstances?: RelatedInstance[];
    currentAdjustment: number;
    triggerRef: { current: HTMLElement | null | undefined } | null;
    priceFlags: Record<AdjustmentScope, ScopePriceFlags>;
}

const BaseModal = BaseModalJS as unknown as (props: { children: ReactNode }) => ReactElement;

const AdvancedAdjustmentPopover = ({
    isOpen,
    onClose,
    onApply,
    product,
    plan,
    group,
    relatedInstances,
    currentAdjustment,
    triggerRef,
    priceFlags,
}: AdvancedAdjustmentPopoverProps) => {
    const { t } = useTranslation();
    const formId = useId();
    const titleId = `${formId}-title`;
    const [adjustmentType, setAdjustmentType] = useState<'percentage' | 'target' | 'keep'>('percentage');
    const [percentageValue, setPercentageValue] = useState<string | number>(() => toNumberInputValue(currentAdjustment));
    const [targetPriceValue, setTargetPriceValue] = useState<string | number>(() => toDecimalInputValue(plan?.currentPrice ?? 0));
    const [scope, setScope] = useState<AdjustmentScope>('thisTier');
    const [applyToIncluded, setApplyToIncluded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const { hasIncluded: hasIncludedPrices, hasScalable: hasScalablePrices } = priceFlags[scope];
    const opensWithScalablePrices = priceFlags.thisTier.hasScalable;

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setAdjustmentType(opensWithScalablePrices ? 'percentage' : 'target');
        setScope('thisTier');
        setApplyToIncluded(!opensWithScalablePrices);
        setPercentageValue(toNumberInputValue(currentAdjustment));
        const initialTarget =
            plan !== null && isTiered(plan) ? sumRounded((plan.tierPoints ?? []).filter((tp) => !tp.isIncluded).map((tp) => tp.currentUnitPrice)) : (plan?.currentPrice ?? 0);
        setTargetPriceValue(toDecimalInputValue(opensWithScalablePrices ? initialTarget : 0));
    }, [isOpen, currentAdjustment, plan, opensWithScalablePrices]);

    const { refs, floatingStyles } = useFloating({
        placement: 'right-start',
        strategy: 'fixed',
        middleware: [offset(8), flip({ crossAxis: false, fallbackPlacements: ['left-start', 'bottom-start', 'top-start'] }), shift({ padding: 16 })],
        whileElementsMounted: autoUpdate,
    });

    useLayoutEffect(() => {
        refs.setReference(isMobile ? null : (triggerRef?.current ?? null));
    }, [refs, isMobile, triggerRef]);

    useOutsideClick(isOpen && !isMobile, triggerRef, refs.floating, onClose);

    useEffect(() => {
        if (!isOpen || !isMobile) return;
        const dialog = refs.floating.current?.closest('dialog');
        const handleBackdropClick = (event: MouseEvent) => {
            if (event.target === dialog) onClose();
        };
        dialog?.addEventListener('click', handleBackdropClick);
        return () => dialog?.removeEventListener('click', handleBackdropClick);
    }, [isOpen, isMobile, onClose, refs.floating]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
                return;
            }
            const dialog = refs.floating.current;
            if (event.key !== 'Tab' || !dialog) return;
            const focusable = [...dialog.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)')];
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const outside = !dialog.contains(document.activeElement);
            if (event.shiftKey && (outside || document.activeElement === first)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (outside || document.activeElement === last)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, refs.floating]);

    useEffect(() => {
        if (!isOpen) return;
        const trigger = triggerRef?.current;
        const frame = requestAnimationFrame(() => refs.floating.current?.querySelector<HTMLInputElement>('input.form-control')?.focus({ preventScroll: true }));
        return () => {
            cancelAnimationFrame(frame);
            trigger?.focus({ preventScroll: true });
        };
    }, [isOpen, triggerRef, refs.floating]);

    const isVolume = plan !== null && isTiered(plan);
    const referenceCurrent = useMemo(() => {
        if (isVolume) return sumRounded((plan?.tierPoints ?? []).filter((tp) => !tp.isIncluded).map((tp) => tp.currentUnitPrice));
        return plan?.currentPrice ?? 0;
    }, [plan, isVolume]);

    const isTargetRelative = adjustmentType === 'target' && isRelativeInput(String(targetPriceValue));
    const scalableCount = isVolume ? (plan?.tierPoints ?? []).filter((tp) => !tp.isIncluded).length : 1;

    const spec = useMemo<AdjustmentSpec>(() => {
        if (adjustmentType === 'percentage') return { kind: 'percent', percent: parsePercentInput(String(percentageValue)) };
        if (isTargetRelative) {
            const delta = parsePriceInput(String(targetPriceValue), 0);
            if (referenceCurrent !== 0) return { kind: 'percent', percent: roundToTwoDecimals(((delta * scalableCount) / referenceCurrent) * 100) };
            return { kind: 'delta', delta };
        }
        return { kind: 'percent', percent: roundToTwoDecimals(percentChange(referenceCurrent, parsePriceInput(String(targetPriceValue), referenceCurrent))) };
    }, [adjustmentType, percentageValue, targetPriceValue, isTargetRelative, referenceCurrent, scalableCount]);

    const includedPrice = useMemo<number | null>(() => {
        if (!applyToIncluded) return null;
        const amount = parseDecimalInput(String(targetPriceValue));
        return Number.isNaN(amount) ? null : roundToTwoDecimals(Math.max(0, amount));
    }, [applyToIncluded, targetPriceValue]);

    const displayCurrent = applyToIncluded ? 0 : referenceCurrent;
    const percentageAvailable = hasScalablePrices && !applyToIncluded;

    const newPrice = useMemo(() => {
        if (applyToIncluded) return includedPrice ?? 0;
        return spec.kind === 'delta' ? roundToTwoDecimals(referenceCurrent + spec.delta * scalableCount) : applyAdjustmentSpec(referenceCurrent, spec);
    }, [applyToIncluded, includedPrice, referenceCurrent, spec, scalableCount]);

    const priceChange = roundToTwoDecimals(newPrice - displayCurrent);
    const displayPct = percentChange(displayCurrent, newPrice);

    const handleIncludedToggle = (checked: boolean) => {
        setApplyToIncluded(checked);
        setAdjustmentType('target');
        setTargetPriceValue(toDecimalInputValue(checked ? 0 : referenceCurrent));
    };

    const handleScopeChange = (next: AdjustmentScope) => {
        setScope(next);
        const flags = priceFlags[next];
        if (!flags.hasScalable && !applyToIncluded) handleIncludedToggle(true);
        else if (!flags.hasIncluded && applyToIncluded) handleIncludedToggle(false);
    };

    const handleApply = () => {
        const keepCurrent = adjustmentType === 'keep';
        onApply(applyToIncluded || keepCurrent ? null : spec, scope, keepCurrent ? null : includedPrice, keepCurrent);
        onClose();
    };

    if (!isOpen || !product || !plan) return null;

    const instanceCount = relatedInstances?.length ?? 0;
    const hasMultipleInstances = instanceCount > 0;

    const content = (
        <>
            <div className="d-flex justify-content-between align-items-center gap-12 p-16 border-bottom">
                <h4 id={titleId} className="mb-0 flex-1">
                    {t('priceSimulation.adjustPrice', { name: product.name })}
                </h4>
                <SkynetIconButton icon="close" size={ButtonSize.SMALL} label={t('priceSimulation.close') ?? ''} onClick={onClose} />
            </div>

            <div className="p-16 overflow-auto">
                <div className="mb-16">
                    <div className="body-xs text-secondary bold mb-4">{product.productSku}</div>
                    <div className="body-xs text-secondary">{[group?.name, product.name].filter(Boolean).join(' → ')}</div>
                </div>

                <div className="border rounded-4 px-12 py-10 bg-secondary mb-16">
                    <div className="body-sm mb-4">
                        {t('priceSimulation.currentPrice')}: <strong>{formatTwoDecimals(displayCurrent)} SEK</strong>
                    </div>
                    <div className="body-xs text-secondary">
                        {t('priceSimulation.avgDiscount')}: {plan.averageDiscount !== null ? formatPercent(plan.averageDiscount) : '-'}
                    </div>
                </div>

                {hasMultipleInstances && (
                    <div className="mb-16">
                        <div className="body-xs text-secondary mb-8">{t('priceSimulation.scopeLabel')}</div>
                        <SegmentedControl<AdjustmentScope>
                            options={[
                                { value: 'thisTier', label: t('priceSimulation.thisTierOnly') ?? '' },
                                { value: 'allTiers', label: t('priceSimulation.allTiers', { count: instanceCount + 1 }) ?? '' },
                            ]}
                            value={scope}
                            onChange={handleScopeChange}
                            ariaLabel={t('priceSimulation.scopeLabel') ?? ''}
                            size="sm"
                        />
                    </div>
                )}

                {hasIncludedPrices && (
                    <div className={`checkbox mb-8${hasScalablePrices ? '' : ' disabled'}`}>
                        <input
                            type="checkbox"
                            id={`${formId}-included`}
                            checked={applyToIncluded}
                            disabled={!hasScalablePrices}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleIncludedToggle(e.target.checked)}
                        />
                        <label htmlFor={`${formId}-included`}>{t('priceSimulation.applyToIncluded')}</label>
                    </div>
                )}

                <div className="mb-16">
                    <div className={`radio mb-8${percentageAvailable ? '' : ' disabled'}`}>
                        <input
                            type="radio"
                            id={`${formId}-percentage`}
                            name={`${formId}-adjustmentType`}
                            checked={adjustmentType === 'percentage'}
                            disabled={!percentageAvailable}
                            onChange={() => setAdjustmentType('percentage')}
                        />
                        <label htmlFor={`${formId}-percentage`}>{t('priceSimulation.percentageAdjustment')}</label>
                    </div>
                    {!percentageAvailable && <div className="body-xs text-secondary ml-24 mb-8">{t('priceSimulation.percentageNotForZeroPrices')}</div>}
                    {adjustmentType === 'percentage' && (
                        <div className="ml-24 mb-8">
                            <input
                                type="number"
                                className="form-control"
                                aria-label={t('priceSimulation.percentageAdjustment') ?? undefined}
                                value={percentageValue}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setPercentageValue(e.target.value)}
                                step="0.01"
                            />
                        </div>
                    )}
                    {adjustmentType !== 'keep' && (
                        <div className="body-xs text-secondary ml-24">
                            → {t('priceSimulation.newPrice')}: {formatTwoDecimals(newPrice)} SEK
                        </div>
                    )}
                </div>

                <div>
                    <div className="radio mb-8">
                        <input
                            type="radio"
                            id={`${formId}-target`}
                            name={`${formId}-adjustmentType`}
                            checked={adjustmentType === 'target'}
                            onChange={() => setAdjustmentType('target')}
                        />
                        <label htmlFor={`${formId}-target`}>{t('priceSimulation.targetPrice')}</label>
                    </div>
                    {adjustmentType === 'target' && (
                        <div className="ml-24 mb-8">
                            <input
                                type="text"
                                inputMode="text"
                                className="form-control"
                                aria-label={t('priceSimulation.targetPrice') ?? undefined}
                                value={targetPriceValue}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setTargetPriceValue(e.target.value)}
                                placeholder={t('priceSimulation.targetPricePlaceholder') ?? undefined}
                            />
                        </div>
                    )}
                    {adjustmentType !== 'keep' && (
                        <div className="body-xs text-secondary ml-24">
                            → {t('priceSimulation.change')}: {priceChange >= 0 ? '+' : ''}
                            {formatPercent(displayPct)} ({priceChange >= 0 ? '+' : ''}
                            {formatTwoDecimals(priceChange)} SEK)
                        </div>
                    )}
                </div>

                <div className="radio mt-16">
                    <input type="radio" id={`${formId}-keep`} name={`${formId}-adjustmentType`} checked={adjustmentType === 'keep'} onChange={() => setAdjustmentType('keep')} />
                    <label htmlFor={`${formId}-keep`}>{t('priceSimulation.keepCurrentPrices')}</label>
                </div>
            </div>

            <div className="d-flex justify-content-end gap-8 p-16 border-top">
                <Button onClick={onClose}>{t('priceSimulation.cancel')}</Button>
                <Button onClick={handleApply} option={ButtonOption.PRIMARY}>
                    {t('priceSimulation.apply')}
                </Button>
            </div>
        </>
    );

    const floatingProps = { ref: refs.setFloating } as const;

    if (isMobile) {
        return (
            <BaseModal>
                <div {...floatingProps} className="d-flex flex-column flex-1 min-h-0">
                    {content}
                </div>
            </BaseModal>
        );
    }

    return (
        <div
            {...floatingProps}
            role="dialog"
            aria-modal
            aria-labelledby={titleId}
            className="advanced-adjustment-popover d-flex flex-column bg-default border rounded-8 shadow-30"
            style={floatingStyles}
        >
            {content}
        </div>
    );
};

export default AdvancedAdjustmentPopover;
